import { __extends } from "tslib";
import { MultipleUVTask } from './MultipleUVTask';
import { COMPOSITE_PART, supportComposition } from './CompositionParts';
var EMPTY_TRANSFORM = new Float32Array([1, 1, 1, 1, 0, 0, 0, 0]);
var EMPTY_MATRIX = new Float32Array([
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1,
    // offsets
    0, 0, 0, 0
]);
var TRANSFORM_PART = "\nuniform vec4 uTransformData[2];\n\nvec4 transform(vec4 color) {\n\tif (color.a > 0.0) {\n\t\tcolor.rgb /= color.a;\n\t}\n\n\tcolor *= clamp(uTransformData[0], 0.0, 1.0);\t\n\tcolor += uTransformData[1];\n\n\tcolor.rgb *= clamp(color.a, 0., 1.);\n\treturn color;\n}\n";
var MATRIX_PART = "\nuniform vec4 uTransformData[5];\n\nvec4 transform(vec4 color) {\n\tif (color.a > 0.0) {\n\t\tcolor.rgb /= color.a;\n\t}\n\n\tvec4 ret = uTransformData[4];\n\tret.r += dot(color, uTransformData[0]);\n\tret.g += dot(color, uTransformData[1]);\n\tret.b += dot(color, uTransformData[2]);\n\tret.a += dot(color, uTransformData[3]);\n\n\tret = clamp(ret, 0., 1.);\n\tret.rgb *= ret.a;\n\t\n\treturn ret;\n}\n";
var FRAG = function (mode, composite) { return "\nprecision highp float;\nuniform sampler2D fs0;\n\nvarying vec2 vUv[2];\n\n".concat(mode === 'transform'
    ? TRANSFORM_PART
    : mode === 'matrix'
        ? MATRIX_PART : '', "\n\n").concat(composite
    ? COMPOSITE_PART(composite)
    : '', "\n\nvoid main() {\n\tvec4 color = texture2D(fs0, vUv[0]);\n\tcolor = ").concat(mode ? 'transform(color)' : 'color', ";\t\n    gl_FragColor = ").concat(composite ? 'composite(color)' : 'color', ";\n}"); };
var ColorMatrixTask = /** @class */ (function (_super) {
    __extends(ColorMatrixTask, _super);
    function ColorMatrixTask() {
        var _this = _super.call(this, 2, false) || this;
        _this._dataChanged = false;
        _this._composite = '';
        _this._mode = '';
        _this._focusId = -1;
        return _this;
    }
    ColorMatrixTask.prototype.setCompositeBlend = function (blend) {
        var old = this._composite;
        this._composite = null;
        this.back = null;
        if (supportComposition(blend)) {
            this._composite = blend;
        }
        if (this._composite !== old) {
            this.invalidateProgram();
        }
        return this._composite !== null;
    };
    Object.defineProperty(ColorMatrixTask.prototype, "transform", {
        get: function () {
            return this._transform;
        },
        set: function (value) {
            if (this._transform === value && !value) {
                return;
            }
            this._transform = value;
            this._dataChanged = true;
            if (!this._fragData || this._fragData.length !== 8) {
                this._fragData = new Float32Array(8);
            }
            if (value) {
                this._fragData.set(value._rawData);
                for (var i = 4; i < 8; i++) {
                    this._fragData[i] /= 0xff;
                }
                // disable positive ALPHA offset, because can't allowed in PMA mode
                if (this._fragData[7] > 0)
                    this._fragData[7] = 0;
            }
            else {
                this._fragData.set(EMPTY_TRANSFORM);
            }
            var mode = value ? 'transform' : '';
            if (this._mode !== mode) {
                this._mode = mode;
                this.invalidateProgram();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorMatrixTask.prototype, "matrix", {
        get: function () {
            return this._matrix;
        },
        set: function (value) {
            this._matrix = value;
            this._dataChanged = true;
            if (!this._fragData || this._fragData.length !== 20) {
                this._fragData = new Float32Array(20);
            }
            if (value) {
                for (var i = 0; i < 4; i++) {
                    for (var j = 0; j < 4; j++) {
                        // we should get 4 of 5, because matrix store data as
                        // mulR, mulG, mulB, mulA, offsetR
                        this._fragData[j + i * 4] = value[j + i * 5];
                    }
                    // pack offsets to latest vector
                    this._fragData[16 + i] = value[4 + i * 5] / 0xff;
                }
            }
            else {
                this._fragData.set(EMPTY_MATRIX);
            }
            var mode = value ? 'matrix' : '';
            if (this._mode !== mode) {
                this._mode = mode;
                this.invalidateProgram();
            }
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ColorMatrixTask.prototype, "name", {
        get: function () {
            return 'CopyFilter:' + this._mode + ',c:' + this._composite;
        },
        enumerable: false,
        configurable: true
    });
    ColorMatrixTask.prototype.getFragmentCode = function () {
        return FRAG(this._mode, this._composite);
    };
    ColorMatrixTask.prototype.activate = function (_stage, _projection, _depthTexture) {
        var prog = this._program3D;
        // upload only when a transform a REAL changed or after shader rebound
        if (this._mode && (this._dataChanged || prog.focusId !== this._focusId)) {
            prog.uploadUniform('uTransformData', this._fragData);
        }
        this._focusId = prog.focusId;
        this._dataChanged = false;
        this
            .source
            .getAbstraction(_stage)
            .activate(0);
        if (this._composite && this.back) {
            var sx = this.inputRect.width / this.back.width;
            var sy = this.inputRect.height / this.back.height;
            this.uvMatrices[1].set([
                0, 0,
                sx, sy
            ], 0);
            this
                .back
                .getAbstraction(_stage)
                .activate(1);
        }
        this.uploadVertexData();
    };
    return ColorMatrixTask;
}(MultipleUVTask));
export { ColorMatrixTask };
